import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2, X } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";

export const fmtDate = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });

function DatePicker({ dates, value, onChange }) {
  if (!dates?.length) return <p className="text-red-300 text-sm" data-testid="booking-no-dates">No upcoming dates are open for booking.</p>;
  return (
    <div data-testid="booking-dates">
      <p className="label mb-2">Choose a date</p>
      <div className="grid grid-cols-2 gap-2">
        {dates.map((d) => (
          <button type="button" key={d.date} data-testid={`booking-date-${d.date}`} disabled={d.sold_out} onClick={() => onChange(d.date)}
            className={`rounded-xl border px-3 py-2.5 text-left transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${value === d.date ? "border-cyan bg-cyan/10" : "border-white/12 bg-ink-800 hover:border-white/30"}`}>
            <span className="block text-sm text-white font-medium">{fmtDate(d.date)}</span>
            <span className={`block text-xs ${d.sold_out ? "text-red-300" : "text-cyan/80"}`}>
              {d.sold_out ? "Sold out" : d.remaining == null ? "Spaces available" : `${d.remaining} space${d.remaining === 1 ? "" : "s"} left`}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function BookingModal({ open, onClose, mode, slot, location, bundle, dates, onSoldOut }) {
  const [classDate, setClassDate] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", bundle_code: "", promo_code: "" });
  const [state, setState] = useState("form"); // form | loading | done
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [useBundle, setUseBundle] = useState(false);
  const [usePromo, setUsePromo] = useState(false);
  const [terms, setTerms] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [programme, setProgramme] = useState(null);
  const [addProgramme, setAddProgramme] = useState(false);
  const needTerms = terms?.enabled && mode !== "programme";
  const canAddProgramme = mode === "bundle" && programme?.available && programme.addon_price > 0 && programme.location_id === bundle?.location_id;
  const bundleTotal = (bundle?.price || 0) + (canAddProgramme && addProgramme ? programme.addon_price : 0);

  useEffect(() => {
    api.get("/content/booking-terms").then((r) => setTerms(r.data)).catch(() => {});
    api.get("/programme").then((r) => setProgramme(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (open) { setState("form"); setResult(null); setError(""); setForm({ name: "", email: "", phone: "", bundle_code: "", promo_code: "" }); setUseBundle(false); setUsePromo(false); setAgreed(false); setAddProgramme(false); setClassDate(""); } }, [open]);
  useEffect(() => {
    setClassDate((cur) => ((dates || []).some((d) => d.date === cur && !d.sold_out) ? cur : (dates || []).find((d) => !d.sold_out)?.date || ""));
  }, [dates, open]);
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "single" && !classDate) { setError("Please choose a class date."); return; }
    if (needTerms && !agreed) { setError("Please tick the box to agree to the terms before continuing."); return; }
    setState("loading"); setError("");
    try {
      // Redeeming a bundle credit or a free-class code is free & instant.
      if (mode === "single" && (useBundle || usePromo)) {
        const { data } = await api.post("/checkout", {
          type: "single", name: form.name, email: form.email, phone: form.phone,
          location_id: location.id, class_slug: slot.class_slug, day: slot.day, time: slot.time, class_date: classDate,
          bundle_code: useBundle ? form.bundle_code : undefined,
          promo_code: usePromo ? form.promo_code : undefined });
        setResult(data); setState("done");
        return;
      }
      // Paid single class or bundle purchase → Stripe Checkout.
      const body = mode === "bundle"
        ? { kind: "bundle", bundle_id: bundle.id, add_programme: !!(canAddProgramme && addProgramme), name: form.name, email: form.email, phone: form.phone, origin_url: window.location.origin }
        : mode === "programme"
        ? { kind: "programme", name: form.name, email: form.email, phone: form.phone, origin_url: window.location.origin }
        : { kind: "single", name: form.name, email: form.email, phone: form.phone,
            location_id: location.id, class_slug: slot.class_slug, day: slot.day, time: slot.time, class_date: classDate, origin_url: window.location.origin };
      const { data } = await api.post("/payments/checkout", body);
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
      if (err.response?.status === 409) onSoldOut?.();
      setState("form");
    }
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div data-testid="booking-modal" className="fixed inset-0 z-[300] overflow-y-auto overscroll-contain bg-ink-900/85 backdrop-blur-md"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
          <div className="min-h-full flex items-center justify-center p-4 sm:p-5 py-8">
          <motion.div onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
            className="relative w-full max-w-lg rounded-3xl glass p-6 sm:p-8">
            <button aria-label="Close" data-testid="booking-close" onClick={onClose} className="absolute top-4 right-4 text-ice/60 hover:text-white"><X size={22} /></button>

            {state === "done" ? (
              <div className="text-center py-4" data-testid="booking-success">
                <div className="mx-auto h-16 w-16 rounded-full bg-cyan/15 flex items-center justify-center mb-5"><Check className="text-cyan" size={30} /></div>
                {result?.type === "bundle" ? (
                  <>
                    <h3 className="font-display text-3xl font-bold text-white">Bundle secured.</h3>
                    <p className="text-ice/75 mt-3">{bundle.name} · {result.credits} credits</p>
                    <div className="mt-5 rounded-xl bg-ink-800 border border-white/12 p-4">
                      <p className="label mb-1">Your bundle code</p>
                      <p className="font-display text-2xl text-cyan tracking-wide" data-testid="bundle-code">{result.code}</p>
                    </div>
                    <p className="text-mist text-xs mt-4">Apply this code at checkout on any class, at any location.</p>
                  </>
                ) : (
                  <>
                    <h3 className="font-display text-3xl font-bold text-white">You're booked in.</h3>
                    <p className="text-ice/75 mt-3">{slot.class_name} · {classDate ? fmtDate(classDate) : slot.day} {slot.time}</p>
                    <p className="text-ice/60 mt-1">{location.name}</p>
                    <p className="mt-4 text-cyan font-medium">{result?.paid_with_promo ? "Free class — £0" : result?.paid_with_bundle ? "Paid with 1 bundle credit — £0" : `Paid £${result?.amount}`}</p>
                    {result?.access_info && <div className="mt-5 rounded-xl bg-ink-800 border border-white/12 p-4 text-left" data-testid="booking-access-info"><p className="label mb-1">Getting in</p><p className="text-ice/85 text-sm whitespace-pre-line">{result.access_info}</p></div>}
                    <p className="text-mist text-xs mt-4">A confirmation email is on its way.</p>
                  </>
                )}
                <button data-testid="booking-done" onClick={onClose} className="book-cta mt-8 mx-auto">Done</button>
              </div>
            ) : (
              <form onSubmit={submit}>
                <span className="label text-cyan">{mode === "bundle" ? "Purchase bundle" : mode === "programme" ? "Training programme" : "Book class"}</span>
                <h3 className="font-display text-2xl font-bold text-white mt-2">
                  {mode === "bundle" ? `${bundle?.name} · £${bundle?.price}` : mode === "programme" ? `${programme?.title} · £${programme?.price}` : `${slot?.class_name}`}
                  {mode === "bundle" && bundle?.location_name && <span className="block text-sm font-normal text-cyan mt-1" data-testid="modal-bundle-location">Valid at {bundle.location_name} only</span>}
                </h3>
                {mode === "programme" && <p className="text-ice/60 text-sm mt-1">You'll get an email with a download link for the PDF, which you can use any time.</p>}
                {mode === "single" && <p className="text-ice/60 text-sm mt-1">{slot?.day} {slot?.time} · {location?.name} · £{slot?.price}</p>}

                <div className="space-y-3 mt-6">
                  {mode === "single" && <DatePicker dates={dates} value={classDate} onChange={setClassDate} />}
                  <input data-testid="booking-name" required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl bg-ink-800 border border-white/12 px-4 py-3 text-ice placeholder:text-mist focus:border-cyan/60 outline-none" />
                  <input data-testid="booking-email" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-xl bg-ink-800 border border-white/12 px-4 py-3 text-ice placeholder:text-mist focus:border-cyan/60 outline-none" />
                  <input data-testid="booking-phone" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full rounded-xl bg-ink-800 border border-white/12 px-4 py-3 text-ice placeholder:text-mist focus:border-cyan/60 outline-none" />

                  {canAddProgramme && (
                    <label data-testid="addon-programme" className="flex items-start gap-3 rounded-xl border border-cyan/30 bg-cyan/5 p-4 text-sm text-ice/90 cursor-pointer">
                      <input type="checkbox" data-testid="addon-programme-toggle" checked={addProgramme} onChange={(e) => setAddProgramme(e.target.checked)} className="accent-cyan h-4 w-4 mt-0.5 shrink-0" />
                      <span>Add the <strong className="text-white">{programme.title}</strong> for <strong className="text-cyan">£{programme.addon_price}</strong>{programme.price > programme.addon_price ? <span className="text-mist"> (usually £{programme.price})</span> : null}. The PDF is emailed to you to keep.</span>
                    </label>
                  )}
                  {mode === "single" && (
                    <div className="rounded-xl bg-ink-800/60 border border-white/8 p-4 space-y-3">
                      <label className="flex items-center gap-2 text-sm text-ice/85 cursor-pointer">
                        <input type="checkbox" data-testid="use-bundle-toggle" checked={useBundle} onChange={(e) => { setUseBundle(e.target.checked); if (e.target.checked) setUsePromo(false); }} className="accent-cyan h-4 w-4" />
                        I have a bundle or 1:1 code
                      </label>
                      {useBundle && (
                        <input data-testid="booking-bundle-code" placeholder="IAM-XXXXXXXX" value={form.bundle_code} onChange={(e) => setForm({ ...form, bundle_code: e.target.value })} className="w-full rounded-lg bg-ink-900 border border-white/12 px-4 py-2.5 text-ice placeholder:text-mist focus:border-cyan/60 outline-none uppercase" />
                      )}
                      <label className="flex items-center gap-2 text-sm text-ice/85 cursor-pointer">
                        <input type="checkbox" data-testid="use-promo-toggle" checked={usePromo} onChange={(e) => { setUsePromo(e.target.checked); if (e.target.checked) setUseBundle(false); }} className="accent-cyan h-4 w-4" />
                        I have a free class code
                      </label>
                      {usePromo && (
                        <input data-testid="booking-promo-code" placeholder="FREE-XXXXXX" value={form.promo_code} onChange={(e) => setForm({ ...form, promo_code: e.target.value })} className="w-full rounded-lg bg-ink-900 border border-white/12 px-4 py-2.5 text-ice placeholder:text-mist focus:border-cyan/60 outline-none uppercase" />
                      )}
                    </div>
                  )}
                </div>

                {needTerms && (
                  <div className="mt-5 rounded-xl bg-ink-800/60 border border-white/8 p-4" data-testid="booking-terms">
                    {terms.title && <p className="label text-ice/90 mb-2">{terms.title}</p>}
                    {terms.body && <div className="max-h-44 overflow-y-auto pr-2 text-ice/70 text-xs leading-relaxed whitespace-pre-line border-b border-white/8 pb-3 mb-3">{terms.body}</div>}
                    <label className="flex items-start gap-2.5 text-sm text-ice/90 cursor-pointer">
                      <input type="checkbox" data-testid="terms-agree" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="accent-cyan h-4 w-4 mt-0.5 shrink-0" />
                      <span>{terms.checkbox_label}</span>
                    </label>
                  </div>
                )}
                {error && <p className="text-red-300 text-sm mt-3" data-testid="booking-error">{error}</p>}
                <button data-testid="booking-submit" type="submit" disabled={state === "loading" || (needTerms && !agreed) || (mode === "single" && !classDate)} className="book-cta w-full justify-center mt-5 disabled:opacity-50">
                  {state === "loading" ? <Loader2 className="animate-spin" size={16} /> : (mode === "bundle" ? `Pay £${bundleTotal}` : mode === "programme" ? `Pay £${programme?.price}` : (usePromo ? "Book free class" : useBundle ? "Book with credit" : `Pay £${slot?.price}`))}
                </button>
                <p className="text-mist text-[0.7rem] text-center mt-3">{usePromo ? "Free-class code — no payment taken." : useBundle ? "Redeem your bundle credit — no payment taken." : "Secure card payment via Stripe"}</p>
              </form>
            )}
          </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
